import React, { useState, useEffect } from 'react';
import { NockchainGrpcService, GrpcError } from '../services/grpc';

interface BlockEntry {
  blockId: string;
  height: number;
  parent: string;
  timestamp: number;
  txIds: string[];
}

const BlockExplorer: React.FC = () => {
  const [blocks, setBlocks] = useState<BlockEntry[]>([]);
  const [currentHeight, setCurrentHeight] = useState<number>(0);
  const [blockHeight, setBlockHeight] = useState('');
  const [blockId, setBlockId] = useState('');
  const [txId, setTxId] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Load initial blocks on component mount
  useEffect(() => {
    handleGetBlocks();
  }, []);

  const handleGetBlocks = async () => {
    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response = await NockchainGrpcService.getBlocks();
      if (response.result?.blocks) {
        setBlocks(response.result.blocks.blocksList || []);
        setCurrentHeight(response.result.blocks.currentHeight || 0);
      }
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetBlockDetails = async () => {
    const selector = blockHeight.trim() || blockId.trim();
    if (!selector) {
      setError('Please enter either a block height or block ID');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const request: any = {};
      if (blockHeight.trim()) {
        request.height = parseInt(blockHeight.trim());
      } else {
        request.blockId = blockId.trim();
      }

      const response = await NockchainGrpcService.getBlockDetails(request);
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetTransactionBlock = async () => {
    if (!txId.trim()) {
      setError('Please enter a transaction ID');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response = await NockchainGrpcService.getTransactionBlock({
        txId: txId.trim()
      });
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGetTransactionDetails = async () => {
    if (!txId.trim()) {
      setError('Please enter a transaction ID');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response = await NockchainGrpcService.getTransactionDetails({
        txId: txId.trim()
      });
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="service-section">
        <h2>Block Explorer</h2>
        <p>Explore blocks, transactions, and blockchain data.</p>

        {/* Blocks List */}
        <div className="request-form">
          <h3>Recent Blocks</h3>
          <button
            className="btn"
            onClick={handleGetBlocks}
            disabled={loading}
          >
            Refresh Blocks
          </button>

          {blocks.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p><strong>Current Height:</strong> {currentHeight}</p>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Height</th>
                    <th>Block ID</th>
                    <th>Timestamp</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.slice(0, 10).map((block, index) => (
                    <tr key={index}>
                      <td>{block.height}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {block.blockId.substring(0, 16)}...
                      </td>
                      <td>{new Date(block.timestamp * 1000).toLocaleString()}</td>
                      <td>{block.txIds.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Get Block Details */}
        <div className="request-form">
          <h3>Get Block Details</h3>
          <div className="form-group">
            <label htmlFor="blockHeight">Block Height</label>
            <input
              id="blockHeight"
              type="number"
              value={blockHeight}
              onChange={(e) => setBlockHeight(e.target.value)}
              placeholder="Enter block height..."
            />
          </div>
          <div style={{ margin: '1rem 0', textAlign: 'center', color: '#666' }}>OR</div>
          <div className="form-group">
            <label htmlFor="blockId">Block ID</label>
            <input
              id="blockId"
              type="text"
              value={blockId}
              onChange={(e) => setBlockId(e.target.value)}
              placeholder="Enter block ID..."
            />
          </div>
          <button
            className="btn"
            onClick={handleGetBlockDetails}
            disabled={loading}
          >
            Get Block Details
          </button>
        </div>

        {/* Transaction Queries */}
        <div className="request-form">
          <h3>Transaction Queries</h3>
          <div className="form-group">
            <label htmlFor="txId">Transaction ID</label>
            <input
              id="txId"
              type="text"
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              placeholder="Enter transaction ID..."
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={handleGetTransactionBlock}
              disabled={loading}
            >
              Get Transaction Block
            </button>
            <button
              className="btn"
              onClick={handleGetTransactionDetails}
              disabled={loading}
            >
              Get Transaction Details
            </button>
          </div>
        </div>
      </div>

      {/* Response Section */}
      {loading && <div className="loading">Loading...</div>}

      {error && (
        <div className="response-section error-response">
          <h3>Error</h3>
          <pre>{error}</pre>
        </div>
      )}

      {response && (
        <div className="response-section">
          <h3>Response</h3>
          <pre className="json-response">{JSON.stringify(response, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default BlockExplorer;
